import { Plus, Search, Users } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { DataTable } from "../../../components/DataTable";
import { Modal } from "../../../components/Modal";
import { Notification } from "../../../components/Notification";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { apiClient } from "../../../lib";
import { AdminLayout } from "../components";

interface Usuario {
  idUsuario: number;
  email: string;
  nombres: string;
  apellidos: string;
  rol: "ALUMNO" | "PROFESOR" | "ADMIN" | "ADMINCOLEGIO";
  fechaCreacion: string;
  rut?: string;
  fechaNacimiento?: string;
  passwordDesencriptada?: string;
  historialAcademico: Array<{
    colegio: {
      idColegio: number;
      nombre: string;
      comuna: {
        nombre: string;
        region: {
          nombre: string;
        };
      };
    };
  }>;
}

interface Colegio {
  idColegio: number;
  nombre: string;
  comuna: {
    nombre: string;
    region: {
      nombre: string;
    };
  };
}

export const UsuariosPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [colegios, setColegios] = useState<Colegio[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  } | null>(null);

  // Filtros
  const [filtroRol, setFiltroRol] = useState("TODOS");
  const [filtroColegio, setFiltroColegio] = useState("todos");
  const [filtroBusqueda, setFiltroBusqueda] = useState("");

  // Formulario
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    nombres: "",
    apellidos: "",
    rut: "",
    fechaNacimiento: "",
    rol: "ALUMNO" as "ALUMNO" | "PROFESOR" | "ADMIN" | "ADMINCOLEGIO",
    colegioId: "sin-asignar",
  });

  const showNotification = useCallback(
    (type: "success" | "error", message: string) => {
      setNotification({
        isOpen: true,
        type,
        title: type === "success" ? "Éxito" : "Error",
        message,
      });
    },
    []
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usuariosRes, colegiosRes] = await Promise.all([
        apiClient.get("/admin/usuarios"),
        apiClient.get("/admin/colegios"),
      ]);

      // Filtrar elementos inválidos y asegurar estructura correcta
      const usuariosData = (usuariosRes.data.data || []).filter(
        (usuario: any) => usuario != null
      );
      const colegiosData = (colegiosRes.data.data || []).filter(
        (colegio: any) => colegio != null
      );
      setUsuarios(usuariosData);
      setColegios(colegiosData);
    } catch (error) {
      console.error("Error cargando datos:", error);
      showNotification("error", "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const loadUsuarios = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filtroRol !== "TODOS") params.append("rol", filtroRol);
      if (filtroColegio && filtroColegio !== "todos")
        params.append("colegioId", filtroColegio);
      if (filtroBusqueda) params.append("search", filtroBusqueda);

      const response = await apiClient.get(`/admin/usuarios?${params}`);
      // Filtrar elementos inválidos y asegurar estructura correcta
      const usuariosData = (response.data.data || []).filter(
        (usuario: any) => usuario != null
      );
      setUsuarios(usuariosData);
    } catch (error) {
      console.error("Error cargando usuarios:", error);
      showNotification("error", "Error cargando usuarios");
    }
  }, [filtroRol, filtroColegio, filtroBusqueda, showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadUsuarios();
  }, [loadUsuarios]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Preparar los datos para enviar al backend
      const dataToSend = {
        email: formData.email,
        password: formData.password || undefined, // Solo enviar password si existe (para edición)
        nombres: formData.nombres,
        apellidos: formData.apellidos,
        rut: formData.rut || undefined,
        fechaNacimiento: formData.fechaNacimiento || undefined,
        rol: formData.rol,
        colegioId:
          formData.colegioId && formData.colegioId !== "sin-asignar"
            ? parseInt(formData.colegioId)
            : undefined,
      };

      if (editingUser) {
        await apiClient.put(
          `/admin/usuarios/${editingUser.idUsuario}`,
          dataToSend
        );
        showNotification("success", "Usuario actualizado exitosamente");
      } else {
        await apiClient.post("/admin/usuarios", dataToSend);
        showNotification("success", "Usuario creado exitosamente");
      }

      setShowModal(false);
      resetForm();
      loadUsuarios();
    } catch (error: any) {
      console.error("Error guardando usuario:", error);
      showNotification(
        "error",
        error.response?.data?.message || "Error guardando usuario"
      );
    }
  };

  const handleEdit = (usuario: Usuario) => {
    setEditingUser(usuario);
    setFormData({
      email: usuario.email || "",
      password: "",
      nombres: usuario.nombres || "",
      apellidos: usuario.apellidos || "",
      rut: usuario.rut || "",
      fechaNacimiento: usuario.fechaNacimiento
        ? new Date(usuario.fechaNacimiento).toISOString().split("T")[0]
        : "",
      rol: usuario.rol || "ALUMNO",
      colegioId:
        usuario.historialAcademico?.[0]?.colegio?.idColegio?.toString() ||
        "sin-asignar",
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    // Reemplazar confirm() con una implementación personalizada
    const userConfirmed = window.confirm(
      "¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer."
    );

    if (!userConfirmed) return;

    try {
      await apiClient.delete(`/admin/usuarios/${id}`);
      showNotification("success", "Usuario eliminado exitosamente");
      loadUsuarios();
    } catch (error) {
      console.error("Error eliminando usuario:", error);
      showNotification("error", "Error eliminando usuario");
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      nombres: "",
      apellidos: "",
      rut: "",
      fechaNacimiento: "",
      rol: "ALUMNO",
      colegioId: "sin-asignar",
    });
    setEditingUser(null);
  };

  const getRolLabel = (rol: string) => {
    switch (rol) {
      case "ALUMNO":
        return "Estudiante";
      case "PROFESOR":
        return "Profesor";
      case "ADMIN":
        return "Administrador";
      case "ADMINCOLEGIO":
        return "Admin Colegio";
      default:
        return rol;
    }
  };

  const getRolColor = (rol: string) => {
    switch (rol) {
      case "ALUMNO":
        return "bg-blue-100 text-blue-800";
      case "PROFESOR":
        return "bg-green-100 text-green-800";
      case "ADMIN":
        return "bg-red-100 text-red-800";
      case "ADMINCOLEGIO":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const columns = [
    {
      key: "nombre",
      label: "Nombre",
      type: "text" as const,
      cell: ({ row }: any) => (
        <div>
          <div className="font-medium">
            {row?.nombres || "-"} {row?.apellidos || ""}
          </div>
          <div className="text-sm text-gray-500">{row?.email || "-"}</div>
        </div>
      ),
    },
    {
      key: "password",
      label: "Contraseña",
      type: "text" as const,
      cell: ({ row }: any) => (
        <div className="text-sm">
          <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">
            {row?.passwordDesencriptada || "No disponible"}
          </span>
        </div>
      ),
    },
    {
      key: "rol",
      label: "Rol",
      type: "text" as const,
      cell: ({ row }: any) => {
        const rol = row?.rol || "ALUMNO";
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getRolColor(
              rol
            )}`}
          >
            {getRolLabel(rol)}
          </span>
        );
      },
    },
    {
      key: "colegio",
      label: "Colegio",
      type: "text" as const,
      cell: ({ row }: any) => {
        const historial = row?.historialAcademico?.[0];
        return historial?.colegio ? (
          <div>
            <div className="font-medium">{historial.colegio.nombre}</div>
            <div className="text-sm text-gray-500">
              {historial.colegio.comuna?.nombre || ""}
              {historial.colegio.comuna?.nombre &&
                historial.colegio.comuna?.region?.nombre &&
                ", "}
              {historial.colegio.comuna?.region?.nombre || ""}
            </div>
          </div>
        ) : (
          <span className="text-gray-400">Sin asignar</span>
        );
      },
    },
    {
      key: "fechaCreacion",
      label: "Fecha Creación",
      type: "text" as const,
      cell: ({ row }: any) => (
        <span className="text-sm text-gray-500">
          {row?.fechaCreacion
            ? new Date(row.fechaCreacion).toLocaleDateString()
            : "-"}
        </span>
      ),
    },
    {
      key: "acciones",
      label: "Acciones",
      type: "actions" as const,
    },
  ];

  const actions = [
    {
      label: "Editar",
      onClick: (item: any) => handleEdit(item),
      variant: "edit" as const,
    },
    {
      label: "Eliminar",
      onClick: (item: any) => handleDelete(item.idUsuario),
      variant: "delete" as const,
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestión de Usuarios
            </h1>
            <p className="text-gray-600">Administra los usuarios del sistema</p>
          </div>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Crear Usuario
          </Button>
        </div>

        {/* Filtros */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="busqueda">Búsqueda</Label>
                <Input
                  id="busqueda"
                  placeholder="Nombre, email..."
                  value={filtroBusqueda}
                  onChange={(e) => setFiltroBusqueda(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="rol">Rol</Label>
                <Select value={filtroRol} onValueChange={setFiltroRol}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TODOS">Todos los roles</SelectItem>
                    <SelectItem value="ALUMNO">Estudiantes</SelectItem>
                    <SelectItem value="PROFESOR">Profesores</SelectItem>
                    <SelectItem value="ADMIN">Administradores</SelectItem>
                    <SelectItem value="ADMINCOLEGIO">Admins Colegio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="colegio">Colegio</Label>
                <Select value={filtroColegio} onValueChange={setFiltroColegio}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los colegios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los colegios</SelectItem>
                    {colegios.map((colegio) => (
                      <SelectItem
                        key={colegio.idColegio}
                        value={colegio.idColegio.toString()}
                      >
                        {colegio.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={loadUsuarios}>
                  <Search className="h-4 w-4 mr-2" />
                  Buscar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabla de usuarios */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usuarios ({usuarios.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              title="Usuarios"
              columns={columns}
              data={usuarios}
              actions={actions}
              pagination={{ enabled: true, itemsPerPage: 15 }}  
            />
          </CardContent>
        </Card>

        {/* Modal de creación/edición */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            resetForm();
          }}
          title={editingUser ? "Editar Usuario" : "Crear Usuario"}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">
                  {editingUser ? "Nueva Contraseña" : "Contraseña *"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required={!editingUser}
                />
              </div>
              <div>
                <Label htmlFor="nombres">Nombres *</Label>
                <Input
                  id="nombres"
                  value={formData.nombres}
                  onChange={(e) =>
                    setFormData({ ...formData, nombres: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="apellidos">Apellidos *</Label>
                <Input
                  id="apellidos"
                  value={formData.apellidos}
                  onChange={(e) =>
                    setFormData({ ...formData, apellidos: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="rut">RUT</Label>
                <Input
                  id="rut"
                  value={formData.rut}
                  onChange={(e) =>
                    setFormData({ ...formData, rut: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="fechaNacimiento">Fecha de Nacimiento</Label>
                <Input
                  id="fechaNacimiento"
                  type="date"
                  value={formData.fechaNacimiento}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fechaNacimiento: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="rol">Rol *</Label>
                <Select
                  value={formData.rol}
                  onValueChange={(value: any) =>
                    setFormData({ ...formData, rol: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALUMNO">Estudiante</SelectItem>
                    <SelectItem value="PROFESOR">Profesor</SelectItem>
                    <SelectItem value="ADMIN">Administrador</SelectItem>
                    <SelectItem value="ADMINCOLEGIO">Admin Colegio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="colegio">Colegio</Label>
                <Select
                  value={formData.colegioId}
                  onValueChange={(value) =>
                    setFormData({ ...formData, colegioId: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar colegio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin-asignar">Sin asignar</SelectItem>
                    {colegios.map((colegio) => (
                      <SelectItem
                        key={colegio.idColegio}
                        value={colegio.idColegio.toString()}
                      >
                        {colegio.nombre} - {colegio.comuna.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingUser ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Notificación */}
        {notification && (
          <Notification
            isOpen={notification.isOpen}
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};
